import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../../common/SEOHead";
import apiService from "../../ApiService";
import { baseUrl } from "../../config";
import { trackEvent } from "../../components/trackingUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilter,
  faClock,
  faArrowRight,
  faBookOpen,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import Reveal from "../../components/Reveal";

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    trackEvent("PageLoad", "", "Blog");
    const fetchBlogPosts = async () => {
      setLoading(true);
      try {
        const data = await apiService.fetchBlog();
        setBlogPosts(data);

        // Extract unique categories
        const uniqueCategories = [
          ...new Set(data.map((post) => post.Category).filter(Boolean)),
        ];
        setCategories(uniqueCategories);

        setError(null);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
        setError("Error fetching blog posts. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch =
      post.Title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.Description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" || post.Category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const SkeletonCard = () => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden animate-pulse">
      <div className="h-56 bg-gray-200 dark:bg-gray-700"></div>
      <div className="p-6 space-y-3">
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        <div className="flex items-center gap-4 pt-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead
        title="GeniusNexa Blog - Expert Technology Insights, AI Trends & Digital Innovation | 2025"
        description="Explore 200+ expert articles on AI, cloud computing, software development, and digital transformation. Get practical tutorials, industry insights, and proven strategies from GeniusNexa's technology experts."
        keywords="technology blog 2025, AI insights, machine learning tutorials, cloud computing guides, digital transformation strategies, software development best practices, tech innovation, developer resources, enterprise solutions, coding tutorials, tech trends analysis"
        image={`${baseUrl}/images/blog-og.jpg`}
        url={`${baseUrl}/blogs`}
        type="website"
      />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "GeniusNexa Technology Blog",
          description:
            "Expert insights on AI, cloud computing, and digital transformation",
          url: `${baseUrl}/blogs`,
          publisher: {
            "@type": "Organization",
            name: "GeniusNexa",
            logo: {
              "@type": "ImageObject",
              url: `${baseUrl}/images/logo.png`,
            },
          },
          blogPost: filteredPosts.slice(0, 10).map((post) => ({
            "@type": "BlogPosting",
            headline: post.Title,
            description: post.Description,
            url: `${baseUrl}/blogs/${post.SEOUrl}`,
            datePublished: post.CreatedDate || post.ModifiedDate,
            dateModified: post.ModifiedDate,
            author: {
              "@type": "Person",
              name: post.Author || "GeniusNexa Team",
            },
            image: post.ImageUrl
              ? `${baseUrl}${post.ImageUrl}`
              : `${baseUrl}/images/blog-og.jpg`,
          })),
        })}
      </script>

      {/* Hero Section */}
      <header className="relative bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] text-white py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="blog-pattern"
                width="30"
                height="30"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="15" cy="15" r="2" fill="currentColor" />
                <circle cx="5" cy="5" r="1" fill="currentColor" opacity="0.5" />
                <circle
                  cx="25"
                  cy="25"
                  r="1"
                  fill="currentColor"
                  opacity="0.5"
                />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#blog-pattern)" />
          </svg>
        </div>

        <div className="absolute inset-0">
          <div className="absolute w-96 h-96 bg-[#E5C558]/10 rounded-full blur-3xl top-0 right-0 animate-pulse"></div>
          <div
            className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl bottom-0 left-0 animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/10 rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/20">
            <FontAwesomeIcon icon={faBookOpen} className="mr-2" />
            Our Blog
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Insights &{" "}
            </span>
            <span className="bg-gradient-to-r from-[#E5C558] to-[#f5d56d] bg-clip-text text-transparent">
              Innovation
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            Explore the latest in AI, cloud technologies, and digital
            transformation. Expert insights, tutorials, and industry trends.
          </p>

          {/* Search Bar */}
          <div className="max-w-3xl mx-auto mb-8">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg"
              />
              <input
                type="text"
                placeholder="Search articles by title, description, or topic..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-5 pl-14 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E5C558] focus:bg-white/15 transition-all text-lg"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-3">
              <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === "all"
                    ? "bg-[#E5C558] text-gray-900 shadow-lg shadow-[#E5C558]/30"
                    : "bg-white/10 text-white hover:bg-white/20 border border-white/20"
                }`}
              >
                All Articles
              </button>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? "bg-[#E5C558] text-gray-900 shadow-lg shadow-[#E5C558]/30"
                      : "bg-white/10 text-white hover:bg-white/20 border border-white/20"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Blog Grid */}
      <div className="container mx-auto px-4 py-20">
        {error && (
          <Reveal>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-6 py-4 rounded-2xl mb-8 text-center shadow-lg">
              <p className="font-medium">{error}</p>
            </div>
          </Reveal>
        )}

        {/* Results Count and Stats */}
        {!loading && (
          <Reveal>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-12 gap-4">
              <div className="text-gray-700 dark:text-gray-300">
                {searchTerm || selectedCategory !== "all" ? (
                  <p className="text-xl font-medium">
                    Found{" "}
                    <span className="text-[#E5C558] font-bold">
                      {filteredPosts.length}
                    </span>{" "}
                    {filteredPosts.length === 1 ? "article" : "articles"}
                  </p>
                ) : (
                  <p className="text-xl font-medium">
                    Showing{" "}
                    <span className="text-[#E5C558] font-bold">
                      {blogPosts.length}
                    </span>{" "}
                    articles
                  </p>
                )}
              </div>

              {selectedCategory !== "all" && (
                <button
                  onClick={() => setSelectedCategory("all")}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-700 dark:text-gray-300"
                >
                  <FontAwesomeIcon icon={faTags} />
                  <span>Clear Category Filter</span>
                </button>
              )}
            </div>
          </Reveal>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {loading ? (
            // Loading Skeletons
            <>
              {[...Array(8)].map((_, idx) => (
                <SkeletonCard key={idx} />
              ))}
            </>
          ) : filteredPosts && filteredPosts.length > 0 ? (
            // Blog Cards
            filteredPosts.map((post, idx) => (
              <Reveal key={idx} delay={0.1 * (idx % 8)}>
                <Link
                  to={`/blogs/${post.SEOUrl}`}
                  onClick={() =>
                    trackEvent("Blog", `View_${post.Title}`, "BlogPage")
                  }
                  className="group bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 dark:border-gray-700 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden h-56 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600">
                    <img
                      src={
                        post.ImageUrl
                          ? `${baseUrl}/${post.ImageUrl}`
                          : `https://via.placeholder.com/400x300/E5C558/0b1220?text=${encodeURIComponent(
                              post.Title || "Blog"
                            )}`
                      }
                      alt={post.Title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = `https://via.placeholder.com/400x300/E5C558/0b1220?text=${encodeURIComponent(
                          post.Title || "Blog"
                        )}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    {/* Category Badge */}
                    {post.Category && (
                      <div className="absolute top-4 left-4 px-3 py-1 bg-[#E5C558] text-gray-900 text-xs font-bold rounded-full shadow-lg">
                        {post.Category}
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-[#E5C558] dark:group-hover:text-[#E5C558] transition-colors line-clamp-2 min-h-[3.5rem]">
                      {post.Title}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-400 line-clamp-3 mb-4 flex-grow">
                      {post.Description}
                    </p>

                    {/* Meta Info */}
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                      {post.Author && (
                        <span className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#E5C558] flex items-center justify-center text-gray-900 text-xs font-bold">
                            {post.Author.charAt(0).toUpperCase()}
                          </div>
                          {post.Author}
                        </span>
                      )}
                      {post.ModifiedDate && (
                        <span className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faClock} className="text-xs" />
                          {new Date(post.ModifiedDate).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center text-[#E5C558] font-semibold group-hover:gap-3 transition-all">
                      Read Article
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="ml-2 group-hover:translate-x-2 transition-transform duration-300"
                      />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))
          ) : (
            // No Results
            <div className="col-span-full text-center py-20">
              <Reveal>
                <div className="max-w-md mx-auto">
                  <div className="mb-6">
                    <svg
                      className="w-24 h-24 mx-auto text-gray-300 dark:text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-700 dark:text-gray-300 mb-3">
                    No articles found
                  </h3>
                  <p className="text-lg text-gray-500 dark:text-gray-400 mb-6">
                    {searchTerm
                      ? "Try adjusting your search terms or browse all articles"
                      : selectedCategory !== "all"
                      ? "No articles in this category yet"
                      : "Check back later for new content"}
                  </p>
                  {(searchTerm || selectedCategory !== "all") && (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCategory("all");
                      }}
                      className="px-6 py-3 bg-[#E5C558] text-gray-900 font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      View All Articles
                    </button>
                  )}
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;
